/**
 * The time cursor: the one instant the whole page is showing. It rests on "now" (`selected` is
 * null and the instant is the clock's), and scrubbing, keys, a tapped day or the radar move it to
 * an hour of the forecast.
 *
 * It is an external store, not React state. Setting it updates the value at once; listeners are
 * told at most once per animation frame, however many times it moved in that frame, and not at
 * all when it ends the frame where it started. Per-frame consumers (a number, a transform, the
 * sky tint) bind with `bindText` / `bindStyle` / `bindCursor` and touch the DOM directly. Parts of
 * the page that only change when the hour changes use `useTimeCursor`, which re-renders on the
 * hour, never per pointer move.
 *
 * Instants are epoch milliseconds on the forecast's hour grid (the slot an hour starts at, which
 * for a half-hour time zone is still a whole UTC hour). The grid and the horizon come from the
 * current hour models (`setHorizon(models.start, models.end)`); without them the cursor reaches
 * from this hour to 47 hours ahead, the shortest series a report carries.
 */
import { useSyncExternalStore } from 'react'

const HOUR = 3_600_000

/** The shortest forecast a report carries is 48 hours: the current hour and the 47 after it. */
const DEFAULT_REACH = 47

export interface TimeCursor {
  /** The chosen hour (epoch ms), or null while the cursor follows the clock. */
  readonly selected: number | null
  /** The instant shown: the chosen hour, or the clock's now. */
  instant(): number
  /** Whether the cursor rests on now. */
  isNow(): boolean
  /** Move to the hour containing `instant`, clamped to the horizon. The hour the clock is in is "now". */
  set(instant: number): void
  /** Move by whole hours (negative = earlier), clamped to the horizon. */
  stepHours(hours: number): void
  /** Back to following the clock. */
  toNow(): void
  /** The first and last hour the cursor may reach, as hour-start instants. Null = the default reach from now. */
  setHorizon(min: number | null, max?: number | null): void
  /** The clock "now" is read from (demo scenes pin their own). */
  setClock(clock: () => number): void
  /** Tell the cursor which place is shown: a change drops back to now and forgets the old horizon, in the same frame. */
  setPlace(id: string | null): void
  /** Called at most once per animation frame after the cursor moved; read `selected` / `instant()` inside. */
  subscribe(listener: () => void): () => void
  /** Run the pending notification now (the place switch, tests). */
  flush(): void
  /** `selected`, for `useSyncExternalStore`. */
  getSnapshot(): number | null
}

export interface CursorOptions {
  clock?: () => number
  /** Frame scheduler (tests inject one). Defaults to requestAnimationFrame, or a 16 ms timer without it. */
  raf?: (callback: () => void) => unknown
  caf?: (handle: unknown) => void
}

export function createTimeCursor(options: CursorOptions = {}): TimeCursor {
  let clock = options.clock ?? Date.now
  const native = !options.raf && typeof requestAnimationFrame === 'function'
  const raf = options.raf ?? (native ? (cb: () => void) => requestAnimationFrame(() => cb()) : (cb: () => void) => setTimeout(cb, 16))
  const caf = options.caf ?? (options.raf ? () => {} : native ? (h: unknown) => cancelAnimationFrame(h as number) : (h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))

  let selected: number | null = null
  let min: number | null = null
  let max: number | null = null
  let place: string | null = null
  let notified: number | null = null
  let handle: unknown = null
  let pending = false
  const listeners = new Set<() => void>()

  /** Start of the hour slot holding `t`, on the grid the horizon sits on (UTC hours before one is set). */
  const slotOf = (t: number) => {
    const origin = min ?? 0
    return origin + Math.floor((t - origin) / HOUR) * HOUR
  }
  const nowSlot = () => slotOf(clock())
  const bounds = (): [number, number] => {
    const lo = min ?? nowSlot()
    return [lo, Math.max(lo, max ?? lo + DEFAULT_REACH * HOUR)]
  }
  const normalize = (t: number): number | null => {
    const [lo, hi] = bounds()
    const slot = Math.min(hi, Math.max(lo, slotOf(t)))
    return slot === nowSlot() ? null : slot
  }

  const run = () => {
    pending = false
    handle = null
    if (notified === selected) return
    notified = selected
    for (const listener of [...listeners]) {
      try {
        listener()
      } catch (e) {
        queueMicrotask(() => {
          throw e
        })
      }
    }
  }
  const schedule = () => {
    if (pending) return
    pending = true
    handle = raf(run)
  }
  const apply = (next: number | null) => {
    if (next === selected) return
    selected = next
    schedule()
  }
  const flush = () => {
    if (!pending) return
    caf(handle)
    run()
  }

  return {
    get selected() {
      return selected
    },
    instant: () => selected ?? clock(),
    isNow: () => selected === null,
    set(instant) {
      if (Number.isFinite(instant)) apply(normalize(instant))
    },
    stepHours(hours) {
      const n = Math.trunc(hours)
      if (!Number.isFinite(n) || n === 0) return
      apply(normalize((selected ?? nowSlot()) + n * HOUR))
    },
    toNow: () => apply(null),
    setHorizon(lo, hi) {
      min = lo != null && Number.isFinite(lo) ? lo : null
      max = hi != null && Number.isFinite(hi) ? hi : null
      // A new horizon may leave the chosen hour outside it (a refreshed, shorter series).
      if (selected !== null) apply(normalize(selected))
    },
    setClock(next) {
      clock = next
    },
    setPlace(id) {
      if (id === place) return
      place = id
      min = null
      max = null
      apply(null)
      flush()
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    flush,
    getSnapshot: () => selected,
  }
}

/** The page's cursor. */
export const timeCursor = createTimeCursor()

/**
 * Follow the app's selected place: switching places drops the cursor back to now in the same
 * frame (the new place has its own hours). `store` is the zustand `useApp`.
 */
export function followPlaces(
  cursor: TimeCursor,
  store: { getState(): { selectedId: string }; subscribe(listener: (state: { selectedId: string }) => void): () => void },
): () => void {
  cursor.setPlace(store.getState().selectedId)
  return store.subscribe((state) => cursor.setPlace(state.selectedId))
}

// ─── React: coarse ───────────────────────────────────────────────────────────

/**
 * The chosen hour (epoch ms), or null on now. Re-renders only when the hour changes — for parts
 * of the page that swap content per hour, not per pointer move.
 */
export function useTimeCursor(cursor: TimeCursor = timeCursor): number | null {
  return useSyncExternalStore(cursor.subscribe, cursor.getSnapshot, cursor.getSnapshot)
}

// ─── Direct DOM: per frame ───────────────────────────────────────────────────

/**
 * Run `apply(value)` now and again whenever `select()` returns something different after the
 * cursor moved (at most once per frame). No React render. Returns the unsubscribe.
 */
export function bindCursor<T>(cursor: TimeCursor, select: () => T, apply: (value: T) => void): () => void {
  let last = select()
  apply(last)
  return cursor.subscribe(() => {
    const next = select()
    if (Object.is(next, last)) return
    last = next
    apply(next)
  })
}

/** Keep a text node (or the single text child of an element) showing `select()`; writes only when the string changed. */
export function bindText(cursor: TimeCursor, node: Node, select: () => string): () => void {
  const text = node.nodeType === 3 ? (node as Text) : node.firstChild?.nodeType === 3 ? (node.firstChild as Text) : null
  return bindCursor(cursor, select, (value) => {
    if (text) text.data = value
    else node.textContent = value
  })
}

/** Keep one inline style property (a `transform`, a custom property) on `select()`. */
export function bindStyle(cursor: TimeCursor, el: HTMLElement, property: string, select: () => string): () => void {
  return bindCursor(cursor, select, (value) => {
    if (property.startsWith('--')) el.style.setProperty(property, value)
    else (el.style as unknown as Record<string, string>)[property] = value
  })
}
